import { Reducer, useCallback, useReducer } from 'react'
import { noop } from 'lodash'
import { modalReducer, ModalState, ModalActionTypes } from './reducer'
import * as stateChangeTypes from './stateChangeTypes'
import { getInitialState } from './utils'

interface UseModalProps<Data> {
  stateReducer: (
    state: ModalState<Data>,
    action: ModalActionTypes<Data>
  ) => ModalState<Data>
  onClose?: () => void
  initialIsOpen?: boolean
}

// Inspiration: https://github.com/downshift-js/downshift/blob/master/README.md
// If you need to modify how this hook works you can refer to the link above
// as an example how to do it

/**
 * Custom hook to handle logic to control Modals. This hook expose a stateReducer
 * property which allow more flexibility for future uses of this hook. The state
 * reducer pattern allows you to define what to do when we dispatch a specific useModal
 * action.
 *
 * For example, if you want to keep the Modal opened when the user click
 * on close but did not change something that was expected. Then you define a
 * reducer with the close action which you listen to and set the isOpen prop to true.
 * That way, the control is shifted to the client (user of this component) and
 * allows us to avoid adding 'if' cases to handle use cases that we did not anticipate
 *
 * @param userProps - Input prop of useModal
 * @param   initialIsOpen - Boolean to control the initial value of isOpen
 * @param   stateReducer - reducer to control internal state for customized use cases
 * @param   onClose - modal close callback
 *
 */
export function useModal<Data>(userProps: Partial<UseModalProps<Data>> = {}) {
  const defaultProps = {
    stateReducer: (state: ModalState<Data>, action: ModalActionTypes<Data>) =>
      action.changes ?? state,
    onClose: noop
  }
  // Props defaults and destructuring.
  const props = {
    ...defaultProps,
    ...userProps
  }
  const { stateReducer, onClose } = props

  // Initial state depending on controlled props.
  const initialState = getInitialState(props)

  // Reducer init.
  const [{ isOpen, openedId, _metadata }, dispatch] = useReducer<
    Reducer<ModalState<Data>, ModalActionTypes<Data>>
  >((state: ModalState<Data>, action: ModalActionTypes<Data>) => {
    const changes = modalReducer<Data>(state, action)
    return stateReducer(state, { ...action, changes })
  }, initialState as never)

  const closeModal = useCallback(() => {
    dispatch({
      type: stateChangeTypes.CloseButtonClick
    })
    return _metadata
  }, [_metadata])

  // Defining id as a potential Mouse Event so we can use that function directly
  // in onClick property and not being worried that the click event will set a
  // weird id
  const openModal = useCallback(
    (id?: string | React.MouseEvent | null, data?: Data): void => {
      dispatch({
        type: stateChangeTypes.OpenButtonClick,
        id: typeof id === 'string' ? id : undefined,
        data
      })
      onClose()
    },
    [onClose]
  )

  return {
    // actions.
    openModal,
    closeModal,
    // state.
    isOpen,
    openedId,
    metadata: _metadata
  }
}

useModal.stateChangeTypes = stateChangeTypes
